import type { SipConnectionStatusProps } from './types';
import { ReactComponent as SipAvailableIcon } from 'Assets/icons/sip-available.svg';
import { ReactComponent as SipReconnectingIcon } from 'Assets/icons/sip-reconnecting.svg';
import { ReactComponent as SipUnavailableIcon } from 'Assets/icons/sip-unavailable.svg';
import { MobXProviderContext, observer } from 'mobx-react';
import {
  useConnectionCoordinator,
  useRecoveryCoordinator,
} from 'Modules/softphone/hooks';
import {
  useSipStatusVariant,
  type SipStatusVariant,
} from 'Modules/softphone/hooks/useSipStatusVariant';
import { useSoftphoneCoordinatorStore } from 'Modules/softphone/store';
import React, { useCallback, useContext, useEffect } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { IconButton } from '~/lib/ui/icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/lib/ui/tooltip';
import { SIP_STATUS_MESSAGE } from './index.constants';

const SIP_STATUS_GA_EVENT: Record<SipStatusVariant, string> = {
  available: 'sip_icon_connected',
  reconnecting: 'sip_icon_reconnecting',
  unavailable: 'sip_icon_not_connected',
};

const resolveConnectionStatusMessage = (
  isWebRTCConnected: boolean,
  isWebRTCReconnecting: boolean,
  webRTCRegistrationConcluded: boolean,
  isReconnecting: boolean
): string => {
  if (isReconnecting || isWebRTCReconnecting) {
    return SIP_STATUS_MESSAGE.TRYING_TO_RECONNECT;
  }

  if (isWebRTCConnected && !webRTCRegistrationConcluded) {
    return SIP_STATUS_MESSAGE.REGISTRATION_FAILED;
  }

  if (isWebRTCConnected && !isWebRTCReconnecting) {
    return SIP_STATUS_MESSAGE.AVAILABLE;
  }

  return SIP_STATUS_MESSAGE.UNAVAILABLE;
};

const SIP_STATUS_ICON: Record<
  SipStatusVariant,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  available: SipAvailableIcon,
  reconnecting: SipReconnectingIcon,
  unavailable: SipUnavailableIcon,
};

export const SipConnectionStatus: React.FC<SipConnectionStatusProps> = observer(
  ({ testid }) => {
    const rootStore = useContext<RootStoreProps>(MobXProviderContext);
    const { person } = rootStore;
    const personId = person?.loggedInPersonId;

    const coordinatorStore = useSoftphoneCoordinatorStore(personId);
    const isNewSoftphoneEnabled = coordinatorStore(
      (state) => state.isNewSoftphoneEnabled
    );

    const {
      isWebRTCConnected,
      isWebRTCReconnecting,
      webRTCRegistrationConcluded,
    } = useConnectionCoordinator();
    const { reconnect, isReconnecting } = useRecoveryCoordinator();
    const { config } = rootStore;

    const handleReconnect = useCallback(() => {
      const shouldReconnect =
        (!isWebRTCConnected && !isWebRTCReconnecting) ||
        !webRTCRegistrationConcluded;

      if (!shouldReconnect) {
        console.log('Reconnect skipped');
        return;
      }

      if (isNewSoftphoneEnabled) {
        void reconnect()
          .then(() => {
            console.log('NEW softphone reconnect completed successfully');
          })
          .catch((error) => {
            console.error('NEW softphone reconnect failed:', error);
          });
        return;
      }

      void config.startConfigRecovery();
    }, [
      isNewSoftphoneEnabled,
      isWebRTCConnected,
      isWebRTCReconnecting,
      webRTCRegistrationConcluded,
      reconnect,
      config,
    ]);

    const statusVariant = useSipStatusVariant();

    useEffect(() => {
      pushToGTMDataLayer(SIP_STATUS_GA_EVENT[statusVariant]);
    }, [statusVariant]);

    const StatusIcon = SIP_STATUS_ICON[statusVariant];
    const isClickable = statusVariant !== 'available';
    const message = resolveConnectionStatusMessage(
      isWebRTCConnected,
      isWebRTCReconnecting,
      webRTCRegistrationConcluded,
      isReconnecting
    );

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`mr-[5px]${!isClickable ? ' cursor-default' : ''}`}
            data-testid={`${testid}-buttonSipConnectionStatus`}
            data-content={message}
            data-icon={`sip-${statusVariant}`}
            data-status-variant={statusVariant}
            data-active={String(statusVariant === 'available')}
          >
            {isClickable ? (
              <IconButton
                icon={StatusIcon}
                iconSize={22}
                onClick={handleReconnect}
                variant="ghost"
                disabled={false}
                // `ariaLabel`, not `aria-label` — IconButton takes no rest
                // props, and a raw hyphenated attribute is silently dropped.
                ariaLabel="SIP Connection Status"
                buttonClassName="hover:bg-transparent"
              />
            ) : (
              <div
                className="flex items-center justify-center rounded-full size-[40px]"
                role="img"
                aria-label="SIP Connection Status"
              >
                <StatusIcon
                  width={22}
                  height={22}
                  style={{ width: '22px', height: '22px' }}
                />
              </div>
            )}
          </div>
        </TooltipTrigger>

        <TooltipContent
          side="bottom"
          sideOffset={-2}
          className="text-foreground text-[13px] leading-[18px] font-normal"
        >
          {message}
        </TooltipContent>
      </Tooltip>
    );
  }
);
